import {
  ReactElement,
  MouseEvent,
  KeyboardEvent,
  FocusEvent,
  RefObject,
} from "react";
import { dataStateType, usePopoverContext } from "./usePopoverContext";
import { supportsPopover } from "../../internal/functions/supportsPopover";
import {
  PopoverLegacyTrigger,
  PopoverLegacyTriggerProps,
} from "../PopoverLegacy";

type PopoverTriggerChildrenReturnType = {
  ref: RefObject<HTMLButtonElement>;
  onMouseEnter: (e: MouseEvent) => void;
  onMouseLeave: (e: MouseEvent) => void;
  onClick: (e: MouseEvent) => void;
  onKeyDown: (e: KeyboardEvent) => void;
  onBlur: (e: FocusEvent) => void;
  onFocus: (e: FocusEvent) => void;
  "aria-haspopup": boolean;
  "aria-expanded": boolean;
  "aria-details": HTMLElement["id"];
  /**
   * @deprecated to be removed on next major
   * Use aria-expanded instead
   */
  "data-state": dataStateType;
  "data-test": string;
};

/**
 * Props for the PopoverTrigger component
 */
export type PopoverTriggerProps = {
  /**
   * Render function that receives trigger props and returns a React element
   */
  children: (data: PopoverTriggerChildrenReturnType) => ReactElement;
};

/**
 * PopoverTrigger component for creating custom trigger elements.
 *
 * Features:
 * - Supports custom trigger elements via render prop pattern
 * - Automatic focus management and keyboard interaction
 * - Hover and click event handling
 * - Accessibility attributes (aria-haspopup, aria-expanded, aria-details)
 * - Legacy popover support with fallback
 * - Context integration with parent popover
 *
 * @example
 * <Popover.Trigger>
 *   {(props) => (
 *     <button {...props}>
 *       Click to open popover
 *     </button>
 *   )}
 * </Popover.Trigger>
 *
 * @example
 * <Popover.Trigger>
 *   {(props) => (
 *     <div {...props} role="button" tabIndex={0}>
 *       Custom trigger element
 *     </div>
 *   )}
 * </Popover.Trigger>
 */
export function PopoverTrigger(props: PopoverTriggerProps) {
  const { children } = props;
  const context = usePopoverContext();

  if (!supportsPopover())
    return <PopoverLegacyTrigger {...(props as PopoverLegacyTriggerProps)} />;

  if (!context) return;
  const { element, ...rest } = context.invoker;

  return children({ "data-test": "hello", ...rest });
}
